/* eslint-disable consistent-return */
/* eslint-disable max-len */
/* eslint-disable react/no-array-index-key */
/* eslint-disable jsx-a11y/role-has-required-aria-props */
import { lowerCase } from '@concur/core-ui-shell';
import Icon from '@concur/nui-widgets/Icon';
import Link from '@concur/nui-widgets/Link';
import Tooltip from '@concur/nui-widgets/Tooltip';
import chain from 'chain-function';
import classnamesBind from 'classnames/bind';
import PropTypes from 'prop-types';
import React, { useState, useId } from 'react';
import { withFormatter } from '@concur/nui-intl-runtime';
import { TRANSLATION_TOOL_URL_IDENTIFIER } from '../constants';
import { getSideNavIcon } from '../utils/sideNavIconMap';
import convertJavascriptUrl from '../utils/convertJavascriptUrl';
import Menu from './_Menu';
import { getSidenavItemAnalyticsJson } from '../utils/getSidenavItemAnalyticsJson';
import { triggerSidenavItemAnalyticsClick } from '../utils/triggerSidenavItemAnalyticsClick';

const renderListItemChild = (
    {
        cssBlock,
        children,
        styles,
        hasPopover,
        icon,
        id,
        submenuHeadingId,
        headingId,
        labelId,
        name,
        additionalAnchorProps,
        dropdownProps,
        anchorSpanProps,
        subItems,
        tooltipMessage,
        doNotDescribe,
        isExpandedSideNav,
        toggleMenu,
        expandMenu,
        collapseMenu,
        expanded,
        isSideNavOverlay,
        formatter,
    },
) => {
    const classnames = classnamesBind.bind(styles);

    if (children) {
        const linkContent = (
            <Link
                {...additionalAnchorProps}
                {...dropdownProps}
                role="menuitem"
                aria-describedby={headingId || labelId}
                data-analytics-json={getSidenavItemAnalyticsJson(id)}
            >
                <div className={classnames(`${cssBlock}__link-container`)}>
                    {isExpandedSideNav && icon && (
                        <Icon ariaHidden className={classnames(`${cssBlock}__icon`)} iconName={getSideNavIcon(id)} />
                    )}
                    {children}
                </div>
            </Link>
        );
        return (
            tooltipMessage ? (
                <Tooltip
                    delay={1000}
                    placement={['bottom', 'top', 'right', 'left']}
                    message={tooltipMessage}
                    doNotDescribe={doNotDescribe}
                    outerPopperProps={{
                        showArrow: false,
                        offset: 2,
                    }}
                    innerContainerProps={{
                        className: classnames(`${cssBlock}__tooltip-inner`),
                    }}
                >
                    {linkContent}
                </Tooltip>
            ) : linkContent
        );
    }

    const arrowIcon = expanded ? 'navigation-down-arrow' : 'navigation-right-arrow';

    const iconClasses = classnames(
        `${cssBlock}__icon`,
    );

    const handleKeydown = (event) => {
        switch (event.key) {
        case 'Enter':
        case ' ':
            toggleMenu?.();
            triggerSidenavItemAnalyticsClick(id);
            event.preventDefault();
            event.stopPropagation();
            break;
        case 'ArrowRight':
            expandMenu?.();
            triggerSidenavItemAnalyticsClick(id);
            event.preventDefault();
            event.stopPropagation();
            break;
        case 'ArrowLeft':
            collapseMenu?.();
            event.preventDefault();
            event.stopPropagation();
            break;
        default:
            break;
        }
    };

    if (subItems) {
        const isTabFocusable = hasPopover || isSideNavOverlay || isExpandedSideNav;
        const submenuLabel = isTabFocusable
            ? `${name}, ${formatter.formattedMessage({ id: 'CoreUI.sideNavSubmenuDescription' })}`
            : undefined;
        return (
            <span
                {...dropdownProps}
                data-test={id ? `menu__button-${lowerCase(id)}` : null}
                role="menuitem"
                aria-haspopup="true"
                id={submenuHeadingId}
                tabIndex={isTabFocusable ? '0' : '-1'}
                aria-label={submenuLabel}
                onClick={isExpandedSideNav ? chain(toggleMenu, dropdownProps?.onClick) : dropdownProps?.onClick}
                onKeyDown={isExpandedSideNav ? chain(handleKeydown, dropdownProps?.onKeyDown) : dropdownProps?.onKeyDown}
                data-analytics-json={getSidenavItemAnalyticsJson(id)}
            >
                <div className={classnames(`${cssBlock}__link-container`)}>
                    {isExpandedSideNav && icon && (
                        <Icon ariaHidden className={classnames(`${cssBlock}__icon`)} iconName={getSideNavIcon(id)} />
                    )}
                    {name}
                </div>
                <Icon ariaHidden className={iconClasses} iconName={arrowIcon} />
            </span>
        );
    }

    return (
        <Link
            {...additionalAnchorProps}
            aria-describedby={headingId || labelId}
            data-analytics-json={getSidenavItemAnalyticsJson(id)}
        >
            <div className={classnames(`${cssBlock}__link-container`)}>
                {isExpandedSideNav && icon && (
                    <Icon ariaHidden className={classnames(`${cssBlock}__icon`)} iconName={getSideNavIcon(id)} />
                )}
                {name}
            </div>
            <span {...anchorSpanProps} />
        </Link>
    );
};

const MenuItem = React.forwardRef((props, ref) => {
    const {
        anchorClassName,
        anchorProps,
        children,
        className,
        cssBlock,
        depth = 1,
        expanded: expandedProp,
        formatter,
        hasPopover,
        id,
        icon,
        headingId,
        isActive,
        isDivider,
        isButton,
        isExpandedSideNav,
        isHeading,
        isSubItem,
        isSubItemMenuAlignRight,
        isSubItemMenuUtility,
        itemId,
        labelId,
        name,
        onClick,
        onMouseEnter,
        parentMenuCssBlock,
        subItems,
        styles,
        target,
        url,
        tooltipMessage,
        doNotDescribe,
        activePathArray = [],
        menuButtonRef,
        isSideNavOverlay,
        disableFocusTrap,
        /* eslint-disable react/prop-types */
        lang,
        /* eslint-enable react/prop-types */
        ...otherProps
    } = props;

    const classnames = classnamesBind.bind(styles);

    const [expanded, setExpanded] = useState(expandedProp || activePathArray?.includes(id));

    const toggleMenu = () => setExpanded(!expanded);
    const expandMenu = () => setExpanded(true);
    const collapseMenu = () => setExpanded(false);

    // do not render the Translation Tool menu item
    // it has to stay in the menuData because it is used
    // to determine if the user has the permission, but
    // just don't render it anymore
    if (url?.indexOf(TRANSLATION_TOOL_URL_IDENTIFIER) >= 0) {
        return null;
    }

    if (isDivider) {
        return <hr aria-hidden="true" className={classnames(`${cssBlock}__divider`)} />;
    }

    if (isHeading) {
        return (
            <li
                {...otherProps}
                aria-hidden
                className={classnames(className, `${cssBlock}__listitem`)}
                role="presentation"
                id={id}
            >
                <div className={classnames(`${cssBlock}__link-container`)}>
                    <Icon ariaHidden className={classnames(`${cssBlock}__icon`)} iconName={getSideNavIcon(itemId)} />
                    {children}
                </div>
            </li>
        );
    }

    if (isButton) {
        return (
            <li
                {...otherProps}
                className={classnames(className, `${cssBlock}__listitem`)}
                role="none"
                id={id}
            >
                <div className={classnames(`${cssBlock}__link-container`)}>
                    {children}
                </div>
            </li>
        );
    }

    const classes = classnames(`${cssBlock}__listitem`, className);

    const activeLeaf = id != null && activePathArray?.[activePathArray.length - 1] === id;

    const anchorClasses = classnames(`${cssBlock}__anchor`, anchorClassName, {
        [`${cssBlock}__anchor--active`]: isActive || activeLeaf,
        [`${cssBlock}__anchor--submenu`]: !!subItems,
        [`${cssBlock}__anchor--expanded`]: expanded,
    });

    const anchorSpanClasses = classnames(`${cssBlock}__anchor-span`);

    // if any menu items are trying to use window.open, the target attribute needs to be removed
    const isTargetAttributeSupported = () => !url?.match(/window\.open/);

    const additionalAnchorProps = {
        ...anchorProps,
        ...convertJavascriptUrl(url, id),
        'aria-current': isActive ? 'page' : undefined,
        'aria-label': name,
        className: anchorClasses,
        innerTextClasses: classnames(`${cssBlock}__linktext`),
        'data-test': id ? `menu__anchor-${lowerCase(id)}` : null,
        target: isTargetAttributeSupported() ? target : null,
        tabIndex: '0',
        'data-active': isActive,
        role: 'menuitem',
    };

    const dropdownClasses = classnames(`${cssBlock}__anchor`, anchorClassName, {
        [`${cssBlock}__anchor--active`]: isActive || activeLeaf,
        [`${cssBlock}__anchor--submenu`]: !!subItems,
        [`${cssBlock}__anchor--expanded`]: expanded,
    });

    const dropdownProps = subItems && {
        className: dropdownClasses,
        'data-test': id ? `menu__dropdown-${lowerCase(id)}` : null,
        onClick,
        onMouseEnter,
        'aria-expanded': expanded,
    };

    const anchorSpanProps = {
        className: anchorSpanClasses,
    };

    const subItemMenuClasses = classnames(`${cssBlock}__submenu`, {
        [`${cssBlock}__submenu--expanded`]: expanded,
        [`${cssBlock}__submenu--right`]: isSubItemMenuAlignRight,
        [`${cssBlock}__submenu--utility`]: isSubItemMenuUtility,
        [`${cssBlock}__submenu--level${depth}`]: depth,
    });

    const submenuHeadingId = useId();

    // handle null menu items
    if (!subItems && !url) return;

    return (
        <li
            role="none"
            {...otherProps}
            className={classes}
            data-test={id ? `menu__listitem-${lowerCase(id)}` : null}
            ref={ref}
        >
            {renderListItemChild(
                {
                    cssBlock,
                    children,
                    styles,
                    hasPopover,
                    icon,
                    id,
                    submenuHeadingId,
                    headingId,
                    labelId,
                    name,
                    additionalAnchorProps,
                    dropdownProps,
                    anchorSpanProps,
                    subItems,
                    tooltipMessage,
                    doNotDescribe,
                    isExpandedSideNav,
                    toggleMenu,
                    expandMenu,
                    collapseMenu,
                    expanded,
                    isSideNavOverlay,
                    formatter,
                },
            )}
            {subItems && (isExpandedSideNav ? expanded : true) && (
                <Menu
                    className={subItemMenuClasses}
                    data-test={id ? `menu__listitem-menu-${lowerCase(id)}` : null}
                    id={id}
                    parentMenuCssBlock={parentMenuCssBlock}
                    isExpandedSideNav={isExpandedSideNav}
                    disableFocusTrap={disableFocusTrap}
                    menuButtonRef={menuButtonRef}
                >
                    {subItems.map((item, index) => (
                        <WrappedMenuItem
                            {...item}
                            activePathArray={activePathArray}
                            parentMenuCssBlock={parentMenuCssBlock}
                            styles={styles}
                            cssBlock={cssBlock}
                            depth={depth + 1}
                            key={`subItem${index}`}
                            hasPopover={hasPopover}
                            isSubItem
                            isSubItemMenuUtility={isSubItemMenuAlignRight}
                            labelId={submenuHeadingId}
                            isSideNavOverlay={isSideNavOverlay}
                        />
                    ))}
                </Menu>
            )}
        </li>
    );
});

MenuItem.displayName = 'MenuItem';

MenuItem.apiPropTypes = {
    name: PropTypes.string,
    url: PropTypes.string,
    id: PropTypes.string,
    isActive: PropTypes.bool,
    isDivider: PropTypes.bool,
    subItems: PropTypes.arrayOf(PropTypes.object),
    target: PropTypes.string,
};

MenuItem.propTypes = {
    ...MenuItem.apiPropTypes,
    anchorClassName: PropTypes.string,
    anchorProps: PropTypes.object,
    children: PropTypes.node,
    className: PropTypes.string,
    cssBlock: PropTypes.string,
    depth: PropTypes.number,
    hasPopover: PropTypes.bool,
    headingId: PropTypes.string,
    isHeading: PropTypes.bool,
    isSubItem: PropTypes.bool,
    isSubItemMenuAlignRight: PropTypes.bool,
    labelId: PropTypes.string,
    menuButtonRef: PropTypes.object,
    isSideNavOverlay: PropTypes.bool,
};

const WrappedMenuItem = withFormatter(MenuItem);

export default WrappedMenuItem;
